import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  CssBaseline,
  ThemeProvider,
  Stack,
  Avatar,
  Divider,
  Fade,
} from '@mui/material';
import {
  Info,
  ContactMail,
  Store,
  ShoppingBag,
  CalculateRounded,
} from '@mui/icons-material';
import createAppTheme from '../theme/theme';
import translations from '../i18n/translations';
import AppHeader from '../components/AppHeader';
import AppFooter from '../components/AppFooter';
import MobileDrawer from '../components/MobileDrawer';
import logo from '../images/colored_logo.PNG';

const AboutUs = () => {
  const [language, setLanguage] = useState(localStorage.getItem('lang') || 'en');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const t = translations[language];
  const theme = createAppTheme(true, language === 'ar');
  const navigate = useNavigate();

  const teamMembers = [
    // {
    //   name: t.teamMember1Name || 'John Doe',
    //   role: t.teamMember1Role || 'Founder & CEO',
    //   avatar: 'https://via.placeholder.com/150',
    // },
    // {
    //   name: t.teamMember2Name || 'Jane Smith',
    //   role: t.teamMember2Role || 'Chief Designer',
    //   avatar: 'https://via.placeholder.com/150',
    // },
    {
      name: t.teamMember3Name || 'Ahmed Hamied Hassani',
      role: t.teamMember3Role || 'Operations Manager',
      avatar: 'https://via.placeholder.com/150',
    },
  ];

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ flexGrow: 1, bgcolor: 'background.default', minHeight: '100vh' }}>
        <AppHeader
          language={language}
          setLanguage={setLanguage}
          drawerOpen={drawerOpen}
          setDrawerOpen={setDrawerOpen}
          t={t}
        />
        <MobileDrawer language={language} drawerOpen={drawerOpen} setDrawerOpen={setDrawerOpen} t={t} />

        <Container maxWidth="xl" sx={{ py: { xs: 3, sm: 4, md: 6 } }}>
          <Fade in timeout={500}>
            <Box>
              {/* Hero Section */}
              <Box
                sx={{
                  textAlign: 'center',
                  mb: { xs: 3, sm: 4, md: 6 },
                  px: { xs: 2, sm: 0 },
                }}
              >
                <Typography
                  variant={{ xs: 'h4', sm: 'h3', md: 'h2' }}
                  sx={{
                    fontWeight: 700,
                    color: 'text.primary',
                    mb: 2,
                    fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                  }}
                >
                  {t.aboutUsTitle || 'About Dhahab'}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{
                    maxWidth: 800,
                    mx: 'auto',
                    lineHeight: 1.8,
                    fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                  }}
                >
                  {t.aboutUsDescription ||
                    'Dhahab is dedicated to providing exquisite jewelry and precious metals, combining tradition with modern craftsmanship. Our passion for quality and customer satisfaction drives everything we do.'}
                </Typography>
              </Box>

              {/* Our Story Section */}
              <Card
                sx={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.01) 100%)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  backdropFilter: 'blur(10px)',
                  mb: { xs: 3, sm: 4, md: 6 },
                }}
              >
                <CardContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Info sx={{ color: 'primary.main', fontSize: { xs: '1.5rem', sm: '2rem' } }} />
                    <Typography
                      variant={{ xs: 'h6', sm: 'h5' }}
                      sx={{
                        fontWeight: 700,
                        color: 'text.primary',
                        fontSize: { xs: '1.25rem', sm: '1.5rem' },
                      }}
                    >
                      {t.ourStory || 'Our Story'}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      lineHeight: 1.8,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                    }}
                  >
                    {t.ourStoryDescription ||
                      'Founded in Kuwait, Dhahab began with a vision to make premium jewelry accessible to everyone. Over the years, we have grown into a trusted name in the industry, known for our commitment to quality, authenticity, and exceptional customer service.'}
                  </Typography>
                </CardContent>
              </Card>

              {/* Our Team Section */}
              <Typography
                variant={{ xs: 'h5', sm: 'h4' }}
                sx={{
                  fontWeight: 700,
                  textAlign: 'center',
                  mb: { xs: 2, sm: 3, md: 4 },
                  fontSize: { xs: '1.25rem', sm: '1.75rem', md: '2rem' },
                }}
              >
                {t.ourTeam || 'Meet Our Team'}
              </Typography>
              <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }} justifyContent="center">
                {teamMembers.map((member, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Fade in timeout={500 + index * 200}>
                      <Card
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          background: 'linear-gradient(135deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.01) 100%)',
                          border: '1px solid rgba(255,255,255,0.08)',
                          backdropFilter: 'blur(10px)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: { xs: 'none', sm: 'translateY(-2px)' },
                            boxShadow: { xs: 'none', sm: theme.shadows[4] },
                          },
                        }}
                      >
                        <CardContent sx={{ textAlign: 'center', py: { xs: 2, sm: 3 } }}>
                          <Avatar
                            src={member.avatar}
                            sx={{
                              width: { xs: 80, sm: 100 },
                              height: { xs: 80, sm: 100 },
                              mb: 2,
                              mx: 'auto',
                              border: '2px solid',
                              borderColor: 'primary.main',
                            }}
                          />
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 600,
                              mb: 1,
                              fontSize: { xs: '1rem', sm: '1.25rem' },
                            }}
                          >
                            {member.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              fontSize: { xs: '0.875rem', sm: '1rem' },
                            }}
                          >
                            {member.role}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Fade>
                  </Grid>
                ))}
              </Grid>

              {/* Call to Action */}
              <Box
                sx={{
                  textAlign: 'center',
                  mt: { xs: 3, sm: 4, md: 6 },
                  mb: { xs: 3, sm: 4 },
                }}
              >
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Store />}
                  onClick={() => navigate('/store')}
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'black',
                    borderRadius: 3,
                    px: { xs: 2, sm: 3 },
                    py: { xs: 1, sm: 1.5 },
                    fontWeight: 700,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    '&:hover': {
                      bgcolor: 'primary.dark',
                      transform: { xs: 'none', sm: 'translateY(-2px)' },
                      boxShadow: { xs: theme.shadows[4], sm: theme.shadows[8] },
                    },
                  }}
                >
                  {t.exploreStore || 'Explore Our Store'}
                </Button>
              </Box>
            </Box>
          </Fade>
        </Container>

        <AppFooter t={t} language={language} />
      </Box>
    </ThemeProvider>
  );
};

export default AboutUs;